import { useRef, type ReactNode } from 'react'
import { ArrowDown, ArrowUp, Columns3, EllipsisVertical, Filter } from 'lucide-react'
import { Menu } from '../controls/Menu'
import s from './ViewSettingsMenu.module.css'

export interface ViewSettingsMenuProps {
  /** The active view differs from its saved form — enables Save as / Reset. */
  modified?: boolean
  /** "Save as new view…". Absent ⇒ this host has no view write door. */
  onSaveAs?: () => void
  onRename?: () => void
  onSetDefault?: () => void
  /** "Reset to saved" — discards the draft. */
  onReset?: () => void
  /** Show as table / cards — only the presentations the declaration allows. */
  presentation?: {
    value: string
    options: readonly { value: string; label: string; icon?: ReactNode }[]
    onChange: (value: string) => void
  }
  wrap?: { checked: boolean; onChange: (next: boolean) => void }
  /** The personal "Command buttons" toggle (ADR F-84 §3). */
  commandButtons?: { checked: boolean; onChange: (next: boolean) => void }
  /**
   * "Filter by <column>…" — the column filters, reachable from the bar (#2375).
   * A grid that reflows to cards has no column headers and so no column ⋮; this
   * is the route that stays. `onFilter` receives the ⋮ itself, a stable anchor
   * for the host's filter editor once the menu has closed.
   */
  filters?: readonly { key: string; label: string; active?: boolean; onFilter: (anchor: HTMLElement) => void }[]
  /**
   * THE COLUMN VERBS, FROM THE BAR (#2375 repair). When the grid reflows to
   * cards its header — and every column ⋮ — is inert, so the host passes these
   * while `DataGrid` reports `onHeadersHiddenChange(true)`: sort, group and the
   * column chooser, visible and keyboard-reachable here instead.
   */
  sort?: {
    columns: readonly { key: string; label: string }[]
    active: { key: string; direction: 'asc' | 'desc' } | null
    onSort: (key: string, direction: 'asc' | 'desc') => void
  }
  group?: {
    columns: readonly { key: string; label: string }[]
    active: string | null
    onGroup: (key: string | null) => void
  }
  /** "Columns…" — show, hide and reorder; the host's chooser anchors on the ⋮. */
  onColumns?: (anchor: HTMLElement) => void
  onCopyLink?: () => void
  onExportCsv?: () => void
  /** Command buttons beyond the three the bar shows, under a "Commands" heading. */
  commands?: ReactNode
  /** Host-specific items (authoring, density, …), drawn last. */
  children?: ReactNode
  /** The collection, for the accessible name: "View settings for products". */
  subject?: string
  testId?: string
  /** Pass-through for a gallery or design capture. */
  defaultOpen?: boolean
}

/**
 * THE VIEW SETTINGS ⋮ (#2375) — the last control in the collection bar.
 *
 * It replaces the bar's Display and More buttons and the presentation
 * switcher: the view's lifecycle (save as, rename, default, reset), how it is
 * drawn (table or cards, wrapping, the command buttons), and what can be done
 * with it (link, export). It is the SAME kit `Menu` the column ⋮ uses.
 *
 * Every item is a prop, and an absent prop is an absent item — except "Save as
 * new view…" and "Reset to saved", which are DISABLED (not hidden) while the
 * view is unmodified, because their availability is a state the person can
 * change and should be able to find.
 */
export function ViewSettingsMenu({
  modified = false, onSaveAs, onRename, onSetDefault, onReset,
  presentation, wrap, commandButtons, filters, sort, group, onColumns, onCopyLink, onExportCsv,
  commands, children, subject, testId = 'grid-view-settings', defaultOpen,
}: ViewSettingsMenuProps) {
  const viewItems = [onSaveAs, onRename, onSetDefault, onReset].some(Boolean)
  const displayItems = (presentation && presentation.options.length > 1) || wrap || commandButtons
  const shareItems = onCopyLink || onExportCsv
  const triggerRef = useRef<HTMLButtonElement>(null)
  /* Choosing a filter hands focus to the host's editor. The hand-off waits for
     the menu's own close-focus moment: opened any earlier, the editor mounts
     inside the still-live modal focus scope, is pulled back into the closing
     menu, and focus ends on <body>. Measured in ViewSettingsMenu.test.tsx. */
  const pendingFilter = useRef<((anchor: HTMLElement) => void) | null>(null)
  const trigger = (
    <button ref={triggerRef} type="button" className={s.trigger} data-testid={testId}
      aria-label={subject ? `View settings for ${subject}` : 'View settings'}>
      <EllipsisVertical size={16} aria-hidden="true" />
    </button>
  )
  const sections: ReactNode[] = []
  if (viewItems) {
    sections.push(
      <div key="view">
        <Menu.Label>View</Menu.Label>
        {onSaveAs && <Menu.Item onSelect={onSaveAs} disabled={!modified} testId={`${testId}-save-as`}>Save as new view…</Menu.Item>}
        {onRename && <Menu.Item onSelect={onRename} testId={`${testId}-rename`}>Rename view…</Menu.Item>}
        {onSetDefault && <Menu.Item onSelect={onSetDefault} testId={`${testId}-set-default`}>Set as default</Menu.Item>}
        {onReset && <Menu.Item onSelect={onReset} disabled={!modified} testId={`${testId}-reset`}>Reset to saved</Menu.Item>}
      </div>,
    )
  }
  if (displayItems) {
    sections.push(
      <div key="display">
        <Menu.Label>Display</Menu.Label>
        {presentation && presentation.options.length > 1 && (
          <Menu.RadioGroup value={presentation.value} onValueChange={presentation.onChange}>
            {presentation.options.map((option) => (
              <Menu.RadioItem key={option.value} value={option.value} testId={`${testId}-show-${option.value}`}>
                {option.icon}{option.label}
              </Menu.RadioItem>
            ))}
          </Menu.RadioGroup>
        )}
        {wrap && (
          <Menu.CheckItem checked={wrap.checked} onSelect={wrap.onChange} testId={`${testId}-wrap`}>
            Wrap cell text
          </Menu.CheckItem>
        )}
        {commandButtons && (
          <Menu.CheckItem checked={commandButtons.checked} onSelect={commandButtons.onChange}
            testId={`${testId}-commands`}>
            Command buttons
          </Menu.CheckItem>
        )}
      </div>,
    )
  }
  if (sort && sort.columns.length > 0) {
    sections.push(
      <div key="sort">
        <Menu.Label>Sort</Menu.Label>
        <Menu.RadioGroup value={sort.active?.key ?? ''}
          onValueChange={(key) => sort.onSort(key, sort.active?.direction ?? 'asc')}>
          {sort.columns.map((column) => (
            <Menu.RadioItem key={column.key} value={column.key} testId={`${testId}-sort-${column.key}`}>
              {column.label}
            </Menu.RadioItem>
          ))}
        </Menu.RadioGroup>
        <Menu.RadioGroup value={sort.active?.direction ?? ''}
          onValueChange={(direction) => { if (sort.active) sort.onSort(sort.active.key, direction as 'asc' | 'desc') }}>
          <Menu.RadioItem value="asc" disabled={!sort.active} testId={`${testId}-sort-asc`}>
            <ArrowUp size={14} aria-hidden="true" /> Ascending
          </Menu.RadioItem>
          <Menu.RadioItem value="desc" disabled={!sort.active} testId={`${testId}-sort-desc`}>
            <ArrowDown size={14} aria-hidden="true" /> Descending
          </Menu.RadioItem>
        </Menu.RadioGroup>
      </div>,
    )
  }
  if (group && group.columns.length > 0) {
    sections.push(
      <div key="group">
        <Menu.Label>Group</Menu.Label>
        <Menu.RadioGroup value={group.active ?? ''} onValueChange={(key) => group.onGroup(key || null)}>
          <Menu.RadioItem value="" testId={`${testId}-group-none`}>Not grouped</Menu.RadioItem>
          {group.columns.map((column) => (
            <Menu.RadioItem key={column.key} value={column.key} testId={`${testId}-group-${column.key}`}>
              Group by {column.label}
            </Menu.RadioItem>
          ))}
        </Menu.RadioGroup>
      </div>,
    )
  }
  if (filters && filters.length > 0) {
    sections.push(
      <div key="filter">
        <Menu.Label>Filter</Menu.Label>
        {filters.map((column) => (
          <Menu.Item key={column.key} testId={`${testId}-filter-${column.key}`}
            onSelect={() => { pendingFilter.current = column.onFilter }}>
            <Filter size={14} aria-hidden="true" />
            {column.label}…{column.active ? ' (filtered)' : ''}
          </Menu.Item>
        ))}
      </div>,
    )
  }
  if (onColumns) {
    sections.push(
      <div key="columns">
        <Menu.Item testId={`${testId}-columns`} onSelect={() => { pendingFilter.current = onColumns }}>
          <Columns3 size={14} aria-hidden="true" /> Columns…
        </Menu.Item>
      </div>,
    )
  }
  if (commands) {
    sections.push(<div key="commands"><Menu.Label>Commands</Menu.Label>{commands}</div>)
  }
  if (shareItems) {
    sections.push(
      <div key="share">
        {onCopyLink && <Menu.Item onSelect={onCopyLink} testId={`${testId}-copy-link`}>Copy link to view</Menu.Item>}
        {onExportCsv && <Menu.Item onSelect={onExportCsv} testId={`${testId}-export`}>Export CSV</Menu.Item>}
      </div>,
    )
  }
  if (children) sections.push(<div key="host">{children}</div>)
  if (sections.length === 0) return null
  return (
    <Menu trigger={trigger} align="end" contentTestId={`${testId}-menu`} defaultOpen={defaultOpen}
      onCloseAutoFocus={(event) => {
        const handOff = pendingFilter.current
        pendingFilter.current = null
        if (!handOff || !triggerRef.current) return
        event.preventDefault()
        handOff(triggerRef.current)
      }}>
      {sections.map((section, index) => (
        <div key={index}>
          {index > 0 && <Menu.Separator />}
          {section}
        </div>
      ))}
    </Menu>
  )
}
